import { useContactPageNotionContent } from "~/pages/contact/_includes/composables/contact-page-notion-content.resolver";
import type { ContactFlowMetadata } from "~~/model/analytics.model";

export type ContactPageFormOptions = {
  additional_information?: boolean;
  tags?: boolean;
  cta_label?: string;
  cta_subtitle?: string;
  resource_url?: string;
};

type ContactPageResolver = {
  check(metadata: ContactFlowMetadata): boolean;
  component: ReturnType<typeof defineAsyncComponent>;
  bindings?: (metadata: ContactFlowMetadata) =>
    | {
        form?: ContactPageFormOptions;
      }
    | undefined;
};

export async function useContactPageResolver(
  metadata: Ref<ContactFlowMetadata>,
): Promise<Pick<ContactPageResolver, "component" | "bindings">> {
  const { getByMetadata } = await useContactPageNotionContent();

  const resolvers: ContactPageResolver[] = [
    {
      check(_metadata) {
        return !!getByMetadata(_metadata);
      },
      component: defineAsyncComponent(
        () =>
          import("../components/content/contact-page-content-notion-entry.vue"),
      ),
      bindings(_metadata) {
        const entry = getByMetadata(_metadata);

        return {
          form: {
            cta_label: entry?.form_cta_label,
            additional_information: entry?.form_additional_information,
            tags: entry?.form_tags,
            resource_url: entry?.resource_url,
          },
        };
      },
    },
    {
      check: (metadata) =>
        (metadata.origin === "course" || metadata.origin === "course_list") &&
        ["download_program_cta", "follow_up_program_cta"].includes(
          metadata.origin_page_action as string,
        ),
      component: defineAsyncComponent(
        () =>
          import(
            "../components/content/contact-page-course-program-download-content.vue"
          ),
      ),
      bindings() {
        return {
          form: {
            tags: true,
          },
        };
      },
    },
    {
      check: (metadata) =>
        metadata.origin === "blog" &&
        metadata.origin_page_action === "download_resource_cta",
      component: defineAsyncComponent(
        () =>
          import(
            "../components/content/contact-page-blog-post-resource-download-content.vue"
          ),
      ),
      bindings() {
        return {
          form: {
            tags: true,
          },
        };
      },
    },
    {
      check: (metadata) =>
        metadata.origin === "events" &&
        metadata.origin_page_action === "apply_cta",
      component: defineAsyncComponent(
        () => import("../components/content/contact-page-event-content.vue"),
      ),
      bindings() {
        return {
          form: { cta_label: "Postuler", tags: true },
        };
      },
    },
    {
      check: (metadata) =>
        metadata.origin === "events" &&
        metadata.origin_page_action === "view_replay_cta",
      component: defineAsyncComponent(
        () => import("../components/content/contact-page-event-content.vue"),
      ),
      bindings() {
        return {
          form: {
            tags: true,
          },
        };
      },
    },
  ];

  const matchingResolver = resolvers.find((resolver) =>
    resolver.check(metadata.value),
  );

  if (matchingResolver) {
    return {
      component: matchingResolver.component,
      bindings: matchingResolver.bindings,
    };
  }

  return {
    component: defineAsyncComponent(
      () => import("../components/content/contact-page-default-content.vue"),
    ),
    bindings() {
      return {
        form: { additional_information: true },
      };
    },
  };
}
